function ignoreTrackingFailure(callback) {
    return (...args) => {
        try {
            return callback(...args)
        } catch {
            // Tracking must never interrupt the app action that triggered it.
        }
    }
}

export function installChurchActivityTracking({ router, initialPage, axios, window, document }) {
    try {
        const endpoint = initialPage?.props?.church_activity?.endpoint
        if (!endpoint || !window.crypto?.randomUUID || !window.AbortController) return null

        const activity = createChurchActivityTracker({
            router, initialPage, endpoint, window, document,
            transport: churchActivityTransport(window, document),
        })
        axios.interceptors.request.use(config => {
            ignoreTrackingFailure(() => {
                const id = activity.correlationId()
                const url = new URL(config.url || '', config.baseURL || window.location.origin)
                if (id && url.origin === window.location.origin) {
                    config.headers ||= {}
                    config.headers['X-Church-Activity-Id'] = id
                }
            })()
            return config
        })
        return activity
    } catch {
        return null
    }
}

export function createChurchActivityTracker({ router, initialPage, transport, endpoint, window, document }) {
    const events = []
    const listeners = []
    let page = initialPage
    let identity = identityFor(page)
    let sending = false
    let disposed = false
    let correlationId = null
    let interactionTime = 0
    let initialNavigation = true
    let retryAt = 0
    let retryDelay = 2000

    function identityFor(currentPage) {
        const user = currentPage?.props?.auth?.user
        return user?.id && user.type === 'church' ? String(user.id) + ':' + (user.team_id ?? '') : null
    }

    function safeIdentifier(value, max = 120) {
        const result = String(value || '').slice(0, max)
        return /^[a-zA-Z0-9_.:/[\]-]+$/.test(result) ? result : undefined
    }

    function safePath(value) {
        try {
            const path = new URL(value, window.location.origin).pathname
            return path.replace(/[^/]*@[^/]*/g, '[redacted]')
                .replace(/\/[a-zA-Z0-9_-]{24,}(?=\/|$)/g, '/[redacted]').slice(0, 300)
        } catch {
            return '/'
        }
    }

    function properties(extra = {}) {
        let queryKeys = []
        try {
            queryKeys = [...new URL(page?.url || window.location.href, window.location.origin).searchParams.keys()]
                .map(key => safeIdentifier(key, 80)).filter(Boolean).slice(0, 30)
        } catch {
            // A malformed URL must not interrupt the interaction being observed.
        }
        return {
            path: safePath(page?.url || window.location.href),
            component: safeIdentifier(page?.component),
            query_keys: queryKeys,
            ...extra,
        }
    }

    function recordPage() {
        record('page.viewed')
        const path = safePath(page?.url || window.location.href)
        const resume = path.match(/^\/candidates\/(\d+)$/)
        const application = path.match(/^\/applications\/(\d+)$/)
        if (resume) record('resume.viewed', { subject_type: 'candidate', subject_id: Number(resume[1]) })
        if (application) record('application.viewed', { subject_type: 'application', subject_id: Number(application[1]) })
    }

    function targetIdentifier(target) {
        const explicit = safeIdentifier(target.dataset?.activityTarget || target.id || target.name)
        if (explicit) return explicit
        if (target.tagName?.toLowerCase() === 'a' && target.getAttribute('href')) {
            return safeIdentifier(safePath(target.getAttribute('href')))
        }
        const parts = []
        for (let node = target; node?.parentElement && parts.length < 5; node = node.parentElement) {
            parts.unshift(node.tagName.toLowerCase() + '.' + Array.from(node.parentElement.children).indexOf(node))
        }
        return safeIdentifier(parts.join(':'))
    }

    const record = ignoreTrackingFailure((eventName, extra = {}) => {
        if (!identity || disposed) return
        if (events.length >= 200) events.shift()
        events.push({
            event_id: window.crypto.randomUUID(),
            event_name: eventName,
            occurred_at: new Date().toISOString(),
            correlation_id: currentCorrelationId(),
            properties: properties(extra),
        })
        if (events.length >= 50) void flush()
    })

    function currentCorrelationId() {
        return identity && Date.now() - interactionTime < 10000 ? correlationId : null
    }

    async function flush({ keepalive = false } = {}) {
        if (disposed || sending || !identity || !events.length || (!keepalive && Date.now() < retryAt)) return
        sending = true
        const batch = events.slice(0, 50)
        const sendingIdentity = identity
        try {
            const response = await transport(endpoint, {
                actor_user_id: page.props.auth.user.id,
                team_id: page.props.auth.user.team_id ?? null,
                events: batch,
            }, { keepalive })
            if (identity !== sendingIdentity) return
            if (response.ok || [401, 403, 409, 419, 422].includes(response.status)) {
                const sentIds = new Set(batch.map(event => event.event_id))
                for (let index = events.length - 1; index >= 0; index--) {
                    if (sentIds.has(events[index].event_id)) events.splice(index, 1)
                }
                retryDelay = 2000
                retryAt = 0
            } else {
                retryAt = Date.now() + retryDelay
                retryDelay = Math.min(retryDelay * 2, 30000)
            }
        } catch {
            retryAt = Date.now() + retryDelay
            retryDelay = Math.min(retryDelay * 2, 30000)
        } finally {
            sending = false
        }
    }

    function on(target, name, callback, options) {
        const listener = ignoreTrackingFailure(callback)
        target.addEventListener(name, listener, options)
        listeners.push(() => target.removeEventListener(name, listener, options))
    }

    function onRouter(name, callback) {
        listeners.push(router.on(name, ignoreTrackingFailure(callback)))
    }

    function interactiveTarget(event) {
        return event.target?.closest?.('a, button, input, select, textarea, [role="button"], [role="tab"], [data-activity-target]')
    }

    on(document, 'click', event => {
        const target = interactiveTarget(event)
        if (!target || target.closest('[data-activity-ignore]')) return
        correlationId = window.crypto.randomUUID()
        interactionTime = Date.now()
        const tag = target.tagName?.toLowerCase()
        record(target.hasAttribute('download') ? 'resume.download_clicked' : 'ui.clicked', {
            target: targetIdentifier(target),
            element: ['a', 'button', 'input', 'select', 'textarea'].includes(tag) ? tag : 'other',
        })
    }, { capture: true, passive: true })

    on(document, 'change', event => {
        const target = interactiveTarget(event)
        if (!target || target.type === 'password' || target.closest('[data-activity-ignore]')) return
        record('ui.changed', {
            field: safeIdentifier(target.name || target.id, 80),
            element: ['input', 'select', 'textarea'].includes(target.tagName?.toLowerCase()) ? target.tagName.toLowerCase() : 'other',
            value_length: Math.min(String(target.value || '').length, 1000000),
            ...(target.type === 'checkbox' ? { checked: target.checked } : {}),
        })
    }, { capture: true, passive: true })

    on(document, 'submit', event => {
        if (event.target?.closest?.('[data-activity-ignore]')) return
        correlationId = window.crypto.randomUUID()
        interactionTime = Date.now()
        record('form.submitted', { element: 'form', target: safeIdentifier(event.target?.id || event.target?.name) })
    }, { capture: true, passive: true })

    on(window, 'church-activity', event => {
        const allowed = ['dialog.opened', 'dialog.closed', 'resume.viewed', 'application.viewed', 'conversation.viewed']
        if (allowed.includes(event.detail?.name)) record(event.detail.name, event.detail.properties || {})
    })

    onRouter('navigate', event => {
        const next = event.detail.page
        const nextIdentity = identityFor(next)
        if (identity !== nextIdentity) {
            events.length = 0
            correlationId = null
            retryAt = 0
        }
        identity = nextIdentity
        const skipInitial = initialNavigation && next.url === initialPage.url && nextIdentity === identityFor(initialPage)
        initialNavigation = false
        page = next
        if (!skipInitial) recordPage()
    })
    onRouter('success', event => {
        const next = event.detail.page
        const nextIdentity = identityFor(next)
        const changed = next.url !== page.url || next.component !== page.component || nextIdentity !== identity
        if (identity !== nextIdentity) {
            events.length = 0
            correlationId = null
            retryAt = 0
        }
        identity = nextIdentity
        page = next
        if (changed) recordPage()
    })
    onRouter('before', event => {
        if (currentCorrelationId()) {
            event.detail.visit.headers ||= {}
            event.detail.visit.headers['X-Church-Activity-Id'] = currentCorrelationId()
        }
    })
    onRouter('error', () => record('form.validation_failed'))

    on(document, 'visibilitychange', () => {
        if (document.visibilityState === 'hidden') void flush({ keepalive: true })
    })
    on(window, 'pagehide', () => void flush({ keepalive: true }))
    on(window, 'online', () => { retryAt = 0; void flush() })

    const timer = window.setInterval(() => void flush(), 2000)
    recordPage()

    return {
        record,
        flush,
        correlationId: currentCorrelationId,
        dispose() {
            disposed = true
            window.clearInterval(timer)
            listeners.forEach(remove => ignoreTrackingFailure(remove)())
            events.length = 0
        },
    }
}

export function emitChurchActivity(name, properties = {}) {
    if (typeof window === 'undefined') return
    ignoreTrackingFailure(() => window.dispatchEvent(new CustomEvent('church-activity', { detail: { name, properties } })))()
}

export function churchActivityTransport(window, document) {
    return async (endpoint, payload, { keepalive }) => {
        const cookie = document.cookie.split('; ').find(value => value.startsWith('XSRF-TOKEN='))
        const token = cookie ? decodeURIComponent(cookie.substring('XSRF-TOKEN='.length)) : null
        const controller = new window.AbortController()
        const timeout = window.setTimeout(() => controller.abort(), 5000)
        try {
            return await window.fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...(token ? { 'X-XSRF-TOKEN': token } : {}),
                },
                body: JSON.stringify(payload),
                keepalive,
                signal: controller.signal,
            })
        } finally {
            window.clearTimeout(timeout)
        }
    }
}
